import { Box, Divider, Grid } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import useSound from 'use-sound'
import { Color } from '../../../../chess/types'
import { Dialogs } from '../../../../sharedComponents/src/Contexts/DialogsContext'
import AnimatedDialog from '../../../../sharedComponents/src/Dialogs/Common/AnimatedDialog'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { GameData, GameResult, GameResultType, LiveGameData, SessionResultHistory } from '../../../../store/types'
import { PlayerEmote } from '../../emotes/EmoteBubble'
import { EmoteButton } from '../../emotes/EmoteButton'
import ClaimedReward from './components/ClaimedReward/ClaimedReward'
import FeedbackMessage from './components/FeedbackMessage/FeedbackMessage'
import { GameOverDescription } from './components/GameOverDescription/GameOverDescription'
import { PlayerColumn } from './components/PlayerColumn/PlayerColumn'
import ResultColumn from './components/ResultColumn/ResultColumn'
import { GameOverNavigation } from './GameOverNavigation/GameOverNavigation'
import { getGameOverData, isADraw, isAWin } from './utils'

interface IProps {
    onClose: () => void
    observer?: boolean
    onRematch?: () => void
    onNewGame?: (vsHuman: boolean) => void
    onAnalysis?: () => void
}

interface IGameOverDialogProps extends IProps {
    gameData: GameData | LiveGameData
    result: GameResult
    myColor?: Color
    sessionResultHistory: Array<SessionResultHistory>
}

const GameOverDialog: React.FC<IGameOverDialogProps> = ({
    onClose,
    onAnalysis,
    onNewGame,
    onRematch,
    observer = false,
    gameData,
    result,
    myColor,
    sessionResultHistory,
}) => {
    const gameInQueue = useStoreState((state) => state.matchMaker.gameInQueue)
    const scoreboards = useStoreState((state) => state.matchMaker.scoreboards)
    const settings = useStoreState((state) => state.gameView.settings)

    const getGameFromQueue = useStoreActions((state) => state.matchMaker.getGameFromQueue)
    const setUpdatedRating = useStoreActions((state) => state.setUpdatedRating)

    const [whiteWins, setWhiteWins] = useState<number>(0)
    const [blackWins, setBlackWins] = useState<number>(0)
    const [draws, setDraws] = useState<number>(0)

    // Game Over Sounds
    const iWonURL = `/assets/sounds/${settings.soundStyle}/GameEndWin.mp3`
    const [playIwon] = useSound(iWonURL, { volume: 0.05 })
    const stalemateURL = `/assets/sounds/${settings.soundStyle}/Stalemate.mp3`
    const [playStalemate] = useSound(stalemateURL, { volume: 0.05 })
    const drawOrLoseSoundURL = `/assets/sounds/${settings.soundStyle}/GameEndDraworLoss.mp3`
    const [playDrawOrLose] = useSound(drawOrLoseSoundURL, { volume: 0.05 })

    const gameOverData = useMemo(() => getGameOverData(result.type, result.winner), [result.type, result.winner])

    useEffect(() => {
        if (!settings.soundOn) return

        if ((myColor && result.winner === myColor) || (observer && result.winner !== undefined)) playIwon()
        else if (result.type === GameResultType.Stalemate) playStalemate()
        else playDrawOrLose()
    }, [settings.soundOn, result, myColor, playIwon, playStalemate, playDrawOrLose])

    const { timeMode, player1, player2, rated, matchId } = gameData

    const [whitePlayer, blackPlayer] = player1.playerColor === 'white' ? [player1, player2] : [player2, player1]

    const whitePlayerOldRating = whitePlayer.ratings?.[timeMode.name]?.rating || 0

    const whitePlayerNewRating = result.newRating
        ? whitePlayer.playerId === result.newRating?.player1_id
            ? result.newRating.user1_rating
            : result.newRating.user2_rating
        : whitePlayerOldRating

    const blackPlayerOldRating = blackPlayer.ratings?.[timeMode.name]?.rating || 0

    const blackPlayerNewRating = result.newRating
        ? blackPlayer.playerId === result.newRating?.player1_id
            ? result.newRating.user1_rating
            : result.newRating.user2_rating
        : blackPlayerOldRating

    useEffect(() => {
        const scoreboard = scoreboards.find((item) => item.pairId === matchId)
        if (!!scoreboard) {
            // PVP game
            const isWhiteChallenger = scoreboard.challenger.playerId === whitePlayer.playerId
            const whiteWinsCount =
                scoreboard.challenger.score.filter((score) => score === (isWhiteChallenger ? 1 : 0)).length +
                (result.winner === Color.White ? 1 : 0)
            const blackWinsCount =
                scoreboard.challenger.score.filter((score) => score === (isWhiteChallenger ? 0 : 1)).length +
                (result.winner === Color.Black ? 1 : 0)
            const drawsCount =
                scoreboard.challenger.score.filter((score) => score === 0.5).length +
                (isADraw({ winnerId: undefined, type: result.type }) ? 1 : 0)

            setWhiteWins(whiteWinsCount)
            setBlackWins(blackWinsCount)
            setDraws(drawsCount)
        } else {
            // PVE game
            const whiteWinsCount = sessionResultHistory.filter((result) => isAWin(result.winnerId, whitePlayer)).length
            const blackWinsCount = sessionResultHistory.filter((result) => isAWin(result.winnerId, blackPlayer)).length
            const drawsCount = sessionResultHistory.filter((result) => isADraw(result)).length
            setWhiteWins(whiteWinsCount)
            setBlackWins(blackWinsCount)
            setDraws(drawsCount)
        }
    }, [scoreboards.length, matchId, result])

    useEffect(() => {
        const myNewRating = myColor === Color.White ? whitePlayerNewRating : blackPlayerNewRating
        if (!observer && rated && myNewRating !== undefined) {
            setUpdatedRating({
                type: timeMode.name,
                newRating: myNewRating,
            })
        }
    }, [])

    const vsHuman = whitePlayer.playerKind === 'human' && blackPlayer.playerKind === 'human'

    const onRematchHandler = () => {
        if (gameInQueue) {
            getGameFromQueue()
        } else {
            onRematch && onRematch()
        }
    }

    return (
        <AnimatedDialog
            name={Dialogs.GAME_OVER}
            onClose={onClose}
            gap="12px"
            topPadding={{ xs: '32px' }}
            paperProps={{
                animationOrImage: {
                    src: `/assets/images/game-over/${gameOverData.header}`,
                    verticalPosition: { xs: '-170px', md: '-230px' },
                    horizontalPosition: { xs: '-3%', md: '15%' },
                },
                closeIcon: {
                    verticalPosition: { xs: '-70px', md: '-45px' },
                    horizontalPosition: { xs: 0, md: '20px' },
                },
                dialogPosition: { xs: 15, md: 0 },
            }}
        >
            <GameOverDescription myColor={myColor} gameOverData={gameOverData} />
            <Divider flexItem />
            <FeedbackMessage result={result} myColor={myColor} observer={observer} />
            <ClaimedReward myColor={myColor} winner={result.winner} rewardURL={result.botdRewardURL} />
            <Grid container spacing="12px" sx={{ position: 'relative' }}>
                <Grid item xs={6} order={1}>
                    <PlayerColumn
                        playerInfo={whitePlayer}
                        winner={result.winner === Color.White}
                        rated={rated}
                        oldRating={whitePlayerOldRating}
                        newRating={whitePlayerNewRating}
                        timeMode={timeMode}
                        emote={!observer && <PlayerEmote playerId={whitePlayer.playerId} placement="above" />}
                    />
                </Grid>
                <Grid item xs={6} order={2}>
                    <PlayerColumn
                        playerInfo={blackPlayer}
                        winner={result.winner === Color.Black}
                        rated={rated}
                        oldRating={blackPlayerOldRating}
                        newRating={blackPlayerNewRating}
                        timeMode={timeMode}
                        emote={!observer && <PlayerEmote playerId={blackPlayer.playerId} placement="above" />}
                    />
                </Grid>
                {!observer && (
                    // PvP emotes stay available while the result is shown: centred between the two avatars
                    <Box
                        sx={{
                            position: 'absolute',
                            zIndex: 4,
                            left: 'calc(50% + 6px)',
                            top: { xs: '32px', md: '54px' },
                            transform: 'translateX(-50%)',
                        }}
                    >
                        <EmoteButton />
                    </Box>
                )}
                <Grid item xs={12} order={3}>
                    <ResultColumn whiteWins={whiteWins} blackWins={blackWins} draws={draws} observer={observer} />
                </Grid>
            </Grid>
            <Divider flexItem />
            <GameOverNavigation
                onAnalysis={onAnalysis}
                onNewGame={onNewGame ? () => onNewGame(vsHuman) : undefined}
                onRematch={onRematch ? onRematchHandler : undefined}
            />
        </AnimatedDialog>
    )
}

const GameOverDialogWrapper: React.FC<IProps> = (props) => {
    const gvGameData = useStoreState((state) => state.gameView.gameData)
    const gvResult = useStoreState((state) => state.gameView.gameResult)
    const ovGameData = useStoreState((state) => state.observeView.liveGameData)
    const ovResult = useStoreState((state) => state.observeView.gameResult)
    const gvMyColor = useStoreState((state) => state.gameView.myColor)
    const gvSessionResultHistory = useStoreState((state) => state.gameView.sessionResultHistory)
    const ovSessionResultHistory = useStoreState((state) => state.observeView.sessionResultHistory)
    const observer = props.observer ?? false
    const gameData = observer ? ovGameData : gvGameData
    const result = observer ? ovResult : gvResult
    const myColor = observer ? undefined : gvMyColor
    const sessionResultHistory = observer ? ovSessionResultHistory : gvSessionResultHistory
    if (!result || !gameData) return null
    return (
        <GameOverDialog
            {...props}
            gameData={gameData}
            result={result}
            myColor={myColor}
            sessionResultHistory={sessionResultHistory}
        />
    )
}

export default GameOverDialogWrapper
